<template>
  <div v-if="visible" class="overlay" @click.self="$emit('close')">
    <div class="modal" :class="{ 'is-shopify': !isShoplazza }">
      <div class="modal-head">
        <div class="modal-icon">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
            <path d="M13.485 1.929a.75.75 0 0 1 0 1.06l-7.778 7.778a.75.75 0 0 1-1.06 0L1.515 6.636a.75.75 0 1 1 1.06-1.06l2.657 2.657 7.248-7.248a.75.75 0 0 1 1.06 0z"/>
          </svg>
        </div>
        <div class="modal-head-text">
          <h3>{{ alreadyBound ? 'App Already Bound' : 'Authorization Confirmed' }}</h3>
          <p v-if="isShoplazza">
            {{ alreadyBound
              ? `This App is already bound to your ${platformLabel} store. You can copy the credentials again or open the settings page.`
              : `Copy the credentials below and paste them into the ${platformLabel} plugin settings to complete setup.` }}
          </p>
        </div>
        <button type="button" class="modal-close" aria-label="Close" @click="$emit('close')">×</button>
      </div>

      <div class="modal-body">
        <div class="panel-left" :class="{ 'is-full': !isShoplazza }">
          <div class="section">
            <p class="section-label">Binding Summary</p>
            <div class="summary-card">
              <div class="summary-row">
                <span class="summary-label">App Name</span>
                <span class="summary-value">{{ appName || '—' }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{ platformLabel }} Store Name</span>
                <span class="summary-value summary-value-muted">{{ storeName || '—' }}</span>
              </div>
              <div v-if="alreadyBound" class="summary-row summary-row-bound">
                <span class="summary-label summary-label-bound">Binding Status</span>
                <span class="summary-bound">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M13.485 1.929a.75.75 0 0 1 0 1.06l-7.778 7.778a.75.75 0 0 1-1.06 0L1.515 6.636a.75.75 0 1 1 1.06-1.06l2.657 2.657 7.248-7.248a.75.75 0 0 1 1.06 0z"/>
                  </svg>
                  Already Bound
                </span>
              </div>
            </div>
          </div>

          <credential-copy-field
            v-if="isShoplazza"
            label="merNo (App ID)"
            :value="merNo"
            :copied="copiedField === 'id'"
            @copy="copyText(merNo, 'id')"
          />

          <credential-copy-field
            v-if="isShoplazza"
            :label="secretKeyLabel"
            :value="secretKeyValue"
            :copied="copiedField === 'key'"
            @copy="copyText(secretKeyValue, 'key')"
          />

          <button v-if="isShoplazza" type="button" class="btn-copy-both" @click="copyBoth">
            <template v-if="copiedField === 'both'">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="icon-success">
                <path d="M13.485 1.929a.75.75 0 0 1 0 1.06l-7.778 7.778a.75.75 0 0 1-1.06 0L1.515 6.636a.75.75 0 1 1 1.06-1.06l2.657 2.657 7.248-7.248a.75.75 0 0 1 1.06 0z"/>
              </svg>
              <span class="copied-text">Copied both!</span>
            </template>
            <template v-else>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25v-7.5z"/>
                <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25v-7.5zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25h-7.5z"/>
              </svg>
              Copy merNo &amp; Sign Key together
            </template>
          </button>

          <button v-if="isShoplazza" type="button" class="btn-green" @click="$emit('open-settings')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z"/>
            </svg>
            Open {{ platformLabel }} App Settings
          </button>
        </div>

        <div v-if="isShoplazza" class="panel-right">
          <setup-guide :platform="platform" :platform-label="platformLabel" />
        </div>
      </div>

      <p v-if="isShopify" class="redirect-hint">
        Return to the Shopify page after {{ countdown }} s...
      </p>
    </div>
  </div>
</template>

<script>
import CredentialCopyField from './CredentialCopyField.vue'
import SetupGuide from './SetupGuide.vue'

export default {
  name: 'ShoplazzaCredentialsModal',
  components: {
    CredentialCopyField,
    SetupGuide,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    appName: {
      type: String,
      default: '',
    },
    storeName: {
      type: String,
      default: '',
    },
    merNo: {
      type: String,
      default: '',
    },
    signKey: {
      type: String,
      default: '',
    },
    appKey: {
      type: String,
      default: '',
    },
    redirect: {
      type: String,
      default: '',
    },
    alreadyBound: {
      type: Boolean,
      default: false,
    },
    platform: {
      type: String,
      default: '',
    },
    platformLabel: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      copiedField: '',
      copyTimer: null,
      countdown: 5,
      countdownTimer: null,
    }
  },
  computed: {
    isShoplazza () {
      return this.platform === 'shoplazza'
    },
    isShopify () {
      return this.platform === 'shopify'
    },
    secretKeyLabel () {
      return this.isShoplazza ? 'Sign Key' : 'App Key'
    },
    secretKeyValue () {
      return this.isShoplazza ? this.signKey : this.appKey
    },
  },
  watch: {
    visible (val) {
      if (!val) {
        this.copiedField = ''
        clearTimeout(this.copyTimer)
        this.clearCountdown()
        return
      }
      if (this.isShopify) {
        this.startCountdown()
      }
    },
  },
  beforeDestroy () {
    clearTimeout(this.copyTimer)
    this.clearCountdown()
  },
  methods: {
    markCopied (field) {
      this.copiedField = field
      clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copiedField = ''
      }, 1800)
    },
    copyText (value, field) {
      if (!value) return
      const done = () => this.markCopied(field)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {
          this.fallbackCopy(value)
          done()
        })
        return
      }
      this.fallbackCopy(value)
      done()
    },
    copyBoth () {
      if (!this.merNo && !this.secretKeyValue) return
      const text = `merNo: ${this.merNo}\nsign key: ${this.secretKeyValue}`
      this.copyText(text, 'both')
    },
    fallbackCopy (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    },
    startCountdown () {
      this.clearCountdown()
      this.countdown = 5
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          this.clearCountdown()
          this.goRedirect()
        }
      }, 1000)
    },
    clearCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    goRedirect () {
      if (!this.redirect) return
      window.location.href = this.redirect
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  overflow: auto;
}

.modal {
  position: relative;
  width: 100%;
  max-width: 1100px;
  max-height: calc(100vh - 48px);
  margin: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: $white;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.22);
  animation: modalIn 0.2s ease;
  flex-shrink: 0;

  &.is-shopify {
    max-width: 520px;
    height: auto;

    .modal-body {
      flex: 0 0 auto;
    }
  }
}

.redirect-hint {
  margin: 0;
  padding: 4px 24px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: $ink-3;
  flex-shrink: 0;
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-head {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, $slate-dark 0%, $slate-mid 100%);
  flex-shrink: 0;
}

.modal-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $green;
  flex-shrink: 0;
}

.modal-head-text {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
  }

  p {
    margin: 2px 0 0;
    font-size: 12px;
    color: #94a3b8;
    line-height: 1.5;
  }
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #94a3b8;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
}

.modal-body {
  display: flex;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.panel-left {
  width: 45%;
  flex-shrink: 0;
  padding: 24px 28px;
  background: $white;
  border-right: 1px solid $border-light;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &.is-full {
    width: 100%;
    border-right: none;
  }
}

.panel-right {
  flex: 1;
  padding: 24px 28px;
  background: rgba(239, 246, 255, 0.4);
  overflow-y: auto;
}

.section-label {
  margin: 0 0 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $ink-4;
}

.summary-card {
  border: 1px solid $border-light;
  border-radius: 12px;
  overflow: hidden;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid $border-light;

  &:last-child {
    border-bottom: none;
    background: $white;
  }
}

.summary-row-bound {
  background: $blue-50 !important;
  border-top: 1px solid $blue-100;
}

.summary-label {
  font-size: 12px;
  color: $ink-3;
}

.summary-label-bound {
  color: $blue-600;
}

.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: $ink;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.summary-value-muted {
  font-size: 12px;
  color: $ink-2;
}

.summary-bound {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: $blue-700;
}

.btn-copy-both {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  border: 1px dashed #93c5fd;
  border-radius: 12px;
  background: $blue-50;
  color: $blue-700;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: $blue-100;
  }
}

.copied-text {
  color: $green-text;
}

.icon-success {
  color: $green-dark;
}

.btn-green {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, $green, $green-dark);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.92;
  }
}

@media (max-width: 900px) {
  .modal-body {
    flex-direction: column;
  }

  .panel-left {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid $border-light;
  }
}
</style>
